import * as React from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import LaunchIcon from '@mui/icons-material/Launch'
import { Theme } from '@mui/material/styles'

import ExtraDetails from './atoms/ExtraDetails'
import BasicInfo from './atoms/BasicInfo'
import { OverviewFields } from './atoms/OverviewFields'
import { PrimaryContactInfo } from './atoms/PrimaryContactInfo'

import { LocationDTO } from '@sales-center/common'
import { EntityDetail } from '@user/store/map/typings'
import { useD2DKnockEnabled } from '@user/store/tenantFeatures/hooks/useD2DKnockEnabled'

type Props = {
  entityDetails: EntityDetail | null
  serviceLocation: LocationDTO
  variant: string
  nameClick?: (entityId: string, variant: string) => void
  viewClick?: (entityId: string, variant: string) => void
  phoneClick?: (entityId: string, variant: string) => void
  handleViewDetailClick: (entityId: string, variant: string) => void
  defaultOpen?: boolean
  canUpdate?: boolean
}

export function EntityLocationDetails({
  entityDetails,
  variant,
  handleViewDetailClick,
  ...rest
}: Props) {
  const isD2dEnabled = useD2DKnockEnabled()

  const isPendingLead = variant === 'pendinglead' || variant === 'draftlead'

  const entityId = entityDetails?.id?.toString() ?? ''

  const detailLabel = React.useMemo(() => {
    if (isPendingLead) return 'Review pending lead'
    if (variant === 'opportunity') return 'View Opportunity Detail Page'
    return 'View Lead Detail Page'
  }, [isPendingLead, variant])

  const handleClick = React.useCallback(() => {
    handleViewDetailClick(entityId, variant)
  }, [handleViewDetailClick, entityId, variant])

  const buttonStyles = React.useMemo(
    () => ({
      color: '#1565C0',
      borderColor: '#1565C080',
    }),
    [],
  )

  return (
    <Box>
      {isD2dEnabled ? (
        <>
          <OverviewFields
            {...rest}
            entityDetails={entityDetails}
            variant={variant}
          />
          <PrimaryContactInfo
            {...rest}
            entityDetails={entityDetails}
            variant={variant}
          />
        </>
      ) : (
        <BasicInfo {...rest} entityDetails={entityDetails} variant={variant} />
      )}

      <ExtraDetails {...rest} entityDetails={entityDetails} variant={variant} />

      {!isPendingLead && (
        <Button
          fullWidth={isD2dEnabled}
          color="primary"
          variant="outlined"
          size={isD2dEnabled ? 'large' : 'small'}
          sx={{
            mt: isD2dEnabled ? 2 : 1.2,
            width: isD2dEnabled ? undefined : 'fit-content',
            ...buttonStyles,
          }}
          onClick={handleClick}
          disableElevation
        >
          {isD2dEnabled ? detailLabel : 'View detail page'}

          <LaunchIcon
            sx={{
              ml: (theme: Theme) => theme.spacing(0.8),
              fontSize: '18px',
            }}
          />
        </Button>
      )}
    </Box>
  )
}
